/**
 * Button shape and section spacing — the two "feel" settings a look sets, and the agent can
 * change.
 *
 * ONE store: the site's existing style row (`site_style_settings.border_radius` /
 * `.section_spacing`, written by PUT /style, un-set by POST /style/reset-field). The values here
 * are that column's own enum, so nothing is translated on the way in or out.
 *
 * Precedence, the same shape as card style (`section_looks.card` over the home look's
 * `cardStyle`): the agent's own choice, else the home look's, else nothing — and "nothing" renders
 * exactly as every site did before these existed. Only a value the agent deliberately chose
 * counts as a choice (the gateway sends it as `style.layout_choice`); an untouched row's column
 * default does not, or no look could ever set these on an existing site.
 *
 * Read by the live site (apps/tenant-sites layout + home page), the Studio preview, and the
 * picker. The CSS that draws them lives in packages/blocks/src/blocks.css.
 */

export type ButtonShape = 'none' | 'subtle' | 'soft' | 'rounded';
export type SectionSpacing = 'compact' | 'standard' | 'relaxed';

export const BUTTON_SHAPES: ReadonlyArray<{ key: ButtonShape; name: string; blurb: string }> = [
  { key: 'none', name: 'Square', blurb: 'Sharp square corners.' },
  { key: 'subtle', name: 'Slightly rounded', blurb: 'Just a touch of rounding.' },
  { key: 'soft', name: 'Rounded', blurb: 'Softly rounded corners. The default.' },
  { key: 'rounded', name: 'Pill', blurb: 'Fully rounded ends.' },
];

export const SECTION_SPACINGS: ReadonlyArray<{ key: SectionSpacing; name: string; blurb: string }> = [
  { key: 'compact', name: 'Compact', blurb: 'Sections sit closer together, so more fits on screen.' },
  { key: 'standard', name: 'Standard', blurb: 'The default space between sections.' },
  { key: 'relaxed', name: 'Airy', blurb: 'Generous space between sections, for a calmer page.' },
];

const SHAPE_KEYS = new Set<string>(BUTTON_SHAPES.map((s) => s.key));
const SPACING_KEYS = new Set<string>(SECTION_SPACINGS.map((s) => s.key));

export function isButtonShape(value: unknown): value is ButtonShape {
  return typeof value === 'string' && SHAPE_KEYS.has(value);
}

export function isSectionSpacing(value: unknown): value is SectionSpacing {
  return typeof value === 'string' && SPACING_KEYS.has(value);
}

/** The button shape to draw: the agent's choice, else the look's, else null (today's buttons). */
export function resolveButtonShape(choice: unknown, lookDefault?: unknown): ButtonShape | null {
  if (isButtonShape(choice)) return choice;
  return isButtonShape(lookDefault) ? lookDefault : null;
}

/** The section spacing to draw: the agent's choice, else the look's, else null (today's spacing). */
export function resolveSectionSpacing(choice: unknown, lookDefault?: unknown): SectionSpacing | null {
  if (isSectionSpacing(choice)) return choice;
  return isSectionSpacing(lookDefault) ? lookDefault : null;
}

/**
 * The two values as root attributes (`data-btn-shape`, `data-spacing`). Absent when null, so a
 * site with neither renders exactly as before.
 */
export function layoutLookAttrs(
  shape: ButtonShape | null,
  spacing: SectionSpacing | null,
): { 'data-btn-shape'?: ButtonShape; 'data-spacing'?: SectionSpacing } {
  return {
    ...(shape ? { 'data-btn-shape': shape } : {}),
    ...(spacing ? { 'data-spacing': spacing } : {}),
  };
}

/**
 * The pair the look picker's live preview draws, from its query values (`?previewButtons=`,
 * `?previewSpacing=`). 'look' means "follow my look" (drop the saved choice); any other value
 * that is not a known option is ignored, falling back to the saved choice and then the look — so
 * a malformed URL can never put an unknown value on the page root.
 */
export function resolvePreviewLayout(
  preview: { buttons?: unknown; spacing?: unknown },
  saved: { borderRadius?: unknown; sectionSpacing?: unknown } | null | undefined,
  lookTheme: { buttonShape?: unknown; spacing?: unknown } | null | undefined,
): { shape: ButtonShape | null; spacing: SectionSpacing | null } {
  return {
    shape: resolveButtonShape(
      preview.buttons === 'look' ? null : isButtonShape(preview.buttons) ? preview.buttons : saved?.borderRadius,
      lookTheme?.buttonShape,
    ),
    spacing: resolveSectionSpacing(
      preview.spacing === 'look' ? null : isSectionSpacing(preview.spacing) ? preview.spacing : saved?.sectionSpacing,
      lookTheme?.spacing,
    ),
  }
}

/** What the gateway sends as `style.layout_choice`: only the values the agent chose. */
export interface LayoutChoice {
  borderRadius: ButtonShape | null;
  sectionSpacing: SectionSpacing | null;
}

/**
 * Builds `layout_choice` from a style row. A column counts only when its field is in
 * `customized_fields` — the existing record of what the agent chose — because the columns carry
 * defaults ('soft' / 'standard') on rows nobody ever set.
 */
export function layoutChoiceFromRow(
  row: { border_radius?: unknown; section_spacing?: unknown; customized_fields?: unknown } | null | undefined,
): LayoutChoice {
  const fields = parseFields(row?.customized_fields);
  return {
    borderRadius: fields.has('layout.borderRadius') && isButtonShape(row?.border_radius) ? row!.border_radius as ButtonShape : null,
    sectionSpacing: fields.has('layout.sectionSpacing') && isSectionSpacing(row?.section_spacing) ? row!.section_spacing as SectionSpacing : null,
  };
}

function parseFields(value: unknown): Set<string> {
  let list: unknown = value;
  if (typeof value === 'string') {
    try { list = JSON.parse(value); } catch { list = []; }
  }
  return new Set(Array.isArray(list) ? list.filter((f): f is string => typeof f === 'string') : []);
}
